import type { LucideIcon } from 'lucide-react';
import { Archive, CheckCircle2, CircleDot, PauseCircle, PlayCircle, XCircle } from 'lucide-react';
import {
  IncidentStatus,
  IncidentPriority,
  IncidentSource,
  IncidentImpact,
  IncidentUrgency,
  EndpointType,
  ProcessingStepType,
  ProcessingStepStatus,
  ResolveReason,
  OnHoldReason,
  CancelReason
} from '../types';
import type { IncidentSlaRecord } from '../types';
import {
  getUserTimezone,
  getUserLocale,
  parseUtcDate,
  formatRelativeTime as formatRelativeDateTime,
} from './dateFormat';

export type Tone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'critical'
  | 'severity-high'
  | 'severity-medium'
  | 'severity-low';

export type StatusTone = 'neutral' | 'info' | 'warning' | 'success' | 'muted';
export type SeverityTone = 'critical' | 'severity-high' | 'severity-medium' | 'severity-low' | 'info';
export type SlaTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

export interface IncidentStatusPresentation {
  label: string;
  tone: StatusTone;
  icon?: LucideIcon;
  dotColorToken: string;
}

export interface IncidentPriorityPresentation {
  label: string;
  tone: SeverityTone;
  dotColorToken: string;
  description: string;
}

export interface SlaStatePresentation {
  label: string;
  tone: SlaTone;
  dotColorToken: string;
}

const INCIDENT_STATUS_PRESENTATION: Record<IncidentStatus, IncidentStatusPresentation> = {
  [IncidentStatus.New]: { label: 'New', tone: 'info', icon: CircleDot, dotColorToken: 'var(--color-status-new-solid)' },
  [IncidentStatus.InProgress]: { label: 'In Progress', tone: 'warning', icon: PlayCircle, dotColorToken: 'var(--color-status-in-progress-solid)' },
  [IncidentStatus.OnHold]: { label: 'On Hold', tone: 'neutral', icon: PauseCircle, dotColorToken: 'var(--color-status-on-hold-solid)' },
  [IncidentStatus.Resolved]: { label: 'Resolved', tone: 'success', icon: CheckCircle2, dotColorToken: 'var(--color-status-resolved-solid)' },
  [IncidentStatus.Cancelled]: { label: 'Cancelled', tone: 'muted', icon: XCircle, dotColorToken: 'var(--color-status-cancelled-solid)' },
  [IncidentStatus.Closed]: { label: 'Closed', tone: 'neutral', icon: Archive, dotColorToken: 'var(--color-status-closed-solid)' },
};

const INCIDENT_PRIORITY_PRESENTATION: Record<IncidentPriority, IncidentPriorityPresentation> = {
  [IncidentPriority.P1]: { label: 'P1', description: 'Critical', tone: 'critical', dotColorToken: 'var(--color-severity-critical-solid)' },
  [IncidentPriority.P2]: { label: 'P2', description: 'High', tone: 'severity-high', dotColorToken: 'var(--color-severity-high-solid)' },
  [IncidentPriority.P3]: { label: 'P3', description: 'Medium', tone: 'severity-medium', dotColorToken: 'var(--color-severity-medium-solid)' },
  [IncidentPriority.P4]: { label: 'P4', description: 'Low', tone: 'severity-low', dotColorToken: 'var(--color-severity-low-solid)' },
};

type SlaStateInput = Pick<IncidentSlaRecord, 'stage' | 'hasBreached' | 'businessElapsedPercentage' | 'isCountingNow'>;

export function getIncidentStatusPresentation(status: IncidentStatus): IncidentStatusPresentation {
  return INCIDENT_STATUS_PRESENTATION[status];
}

export function getIncidentPriorityPresentation(priority: IncidentPriority): IncidentPriorityPresentation {
  return INCIDENT_PRIORITY_PRESENTATION[priority];
}

export function getSlaStatePresentation(state: SlaStateInput): SlaStatePresentation {
  if (state.stage === 'Cancelled') {
    return { label: 'Cancelled', tone: 'neutral', dotColorToken: 'var(--color-sla-cancelled-solid)' };
  }

  if (state.stage === 'Completed') {
    return { label: 'Completed', tone: 'success', dotColorToken: 'var(--color-sla-completed-solid)' };
  }

  if (state.stage === 'Paused' || state.isCountingNow === false) {
    return { label: 'Paused', tone: 'info', dotColorToken: 'var(--color-sla-paused-solid)' };
  }

  if (state.hasBreached) {
    return { label: 'Breached', tone: 'danger', dotColorToken: 'var(--color-sla-breached-solid)' };
  }

  const elapsedPercentage = state.businessElapsedPercentage ?? 0;

  if (elapsedPercentage >= 75) {
    return { label: 'At risk', tone: 'warning', dotColorToken: 'var(--color-sla-at-risk-solid)' };
  }

  return { label: 'Healthy', tone: 'success', dotColorToken: 'var(--color-sla-healthy-solid)' };
}

export function formatDate(date: string | Date): string {
  const d = parseUtcDate(date);
  if (!d) return '—';
  return d.toLocaleString(getUserLocale(), {
    timeZone: getUserTimezone(),
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelativeTime(date: string | Date): string {
  return formatRelativeDateTime(date);
}

export function getStatusLabel(status: IncidentStatus): string {
  return getIncidentStatusPresentation(status).label;
}

export function getStatusColor(status: IncidentStatus): string {
  const colors: Record<IncidentStatus, string> = {
    [IncidentStatus.New]: 'bg-blue-100 text-blue-800',
    [IncidentStatus.InProgress]: 'bg-yellow-100 text-yellow-800',
    [IncidentStatus.OnHold]: 'bg-gray-100 text-gray-800',
    [IncidentStatus.Resolved]: 'bg-green-100 text-green-800',
    [IncidentStatus.Cancelled]: 'bg-gray-100 text-gray-800',
    [IncidentStatus.Closed]: 'bg-gray-100 text-gray-800',
  };
  return colors[status];
}

export function getPriorityLabel(priority: IncidentPriority): string {
  return getIncidentPriorityPresentation(priority).label;
}

export function getImpactLabel(impact: IncidentImpact): string {
  const labels: Record<IncidentImpact, string> = {
    [IncidentImpact.High]: 'High',
    [IncidentImpact.Medium]: 'Medium',
    [IncidentImpact.Low]: 'Low',
  };
  return labels[impact];
}

export function getUrgencyLabel(urgency: IncidentUrgency): string {
  const labels: Record<IncidentUrgency, string> = {
    [IncidentUrgency.High]: 'High',
    [IncidentUrgency.Medium]: 'Medium',
    [IncidentUrgency.Low]: 'Low',
  };
  return labels[urgency];
}

export function getSourceLabel(source: IncidentSource): string {
  const labels: Record<IncidentSource, string> = {
    [IncidentSource.Manual]: 'Manual',
    [IncidentSource.AzureMonitor]: 'Azure Monitor',
    [IncidentSource.Webhook]: 'Webhook',
    [IncidentSource.Email]: 'Email',
    [IncidentSource.API]: 'API',
    [IncidentSource.Teams]: 'Teams',
  };
  return labels[source];
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

export function getEndpointTypeLabel(type: EndpointType): string {
  const labels: Record<EndpointType, string> = {
    [EndpointType.AzureMonitor]: 'Azure Monitor',
    [EndpointType.Datadog]: 'Datadog',
    [EndpointType.Prometheus]: 'Prometheus',
    [EndpointType.Generic]: 'Generic',
    [EndpointType.AwsCloudWatch]: 'AWS CloudWatch',
    [EndpointType.Grafana]: 'Grafana',
    [EndpointType.NewRelic]: 'New Relic',
  };
  return labels[type];
}

function normalizeProcessingStepType(type: ProcessingStepType | number | string): ProcessingStepType | undefined {
  if (typeof type === 'string') {
    if ((Object.values(ProcessingStepType) as string[]).includes(type)) {
      return type as ProcessingStepType;
    }
    return undefined;
  }

  switch (type) {
    case 0:
      return ProcessingStepType.Validation;
    case 1:
      return ProcessingStepType.Normalization;
    case 2:
      return ProcessingStepType.Deduplication;
    case 3:
      return ProcessingStepType.Suppression;
    case 4:
      return ProcessingStepType.Enrichment;
    case 5:
      return ProcessingStepType.Correlation;
    case 6:
      return ProcessingStepType.IncidentCreation;
    case 7:
      return ProcessingStepType.Notification;
    case 8:
      return ProcessingStepType.IncidentGate;
    case 10:
      return ProcessingStepType.Initialization;
    case 99:
      return ProcessingStepType.Custom;
    default:
      return undefined;
  }
}

function normalizeProcessingStepStatus(status: ProcessingStepStatus | number | string): ProcessingStepStatus | undefined {
  if (typeof status === 'string') {
    if ((Object.values(ProcessingStepStatus) as string[]).includes(status)) {
      return status as ProcessingStepStatus;
    }
    return undefined;
  }

  switch (status) {
    case 0:
      return ProcessingStepStatus.InProgress;
    case 1:
      return ProcessingStepStatus.Completed;
    case 2:
      return ProcessingStepStatus.Failed;
    case 3:
      return ProcessingStepStatus.Skipped;
    case 4:
      return ProcessingStepStatus.Pending;
    default:
      return undefined;
  }
}

export function getProcessingStepTypeLabel(type: ProcessingStepType | number | string): string {
  const normalizedType = normalizeProcessingStepType(type);
  if (!normalizedType) {
    return 'Unknown';
  }

  const labels: Record<ProcessingStepType, string> = {
    [ProcessingStepType.Validation]: 'Validation',
    [ProcessingStepType.Normalization]: 'Normalization',
    [ProcessingStepType.Deduplication]: 'Deduplication',
    [ProcessingStepType.Suppression]: 'Suppression',
    [ProcessingStepType.Enrichment]: 'Enrichment',
    [ProcessingStepType.Correlation]: 'Correlation',
    [ProcessingStepType.IncidentGate]: 'Incident Gate',
    [ProcessingStepType.IncidentCreation]: 'Incident Creation',
    [ProcessingStepType.Notification]: 'Notification',
    [ProcessingStepType.Initialization]: 'Initialization',
    [ProcessingStepType.Custom]: 'Custom',
  };
  return labels[normalizedType];
}

export function getProcessingStepStatusLabel(status: ProcessingStepStatus | number | string): string {
  const normalizedStatus = normalizeProcessingStepStatus(status);
  if (!normalizedStatus) {
    return 'Unknown';
  }

  const labels: Record<ProcessingStepStatus, string> = {
    [ProcessingStepStatus.InProgress]: 'In Progress',
    [ProcessingStepStatus.Completed]: 'Completed',
    [ProcessingStepStatus.Failed]: 'Failed',
    [ProcessingStepStatus.Skipped]: 'Skipped',
    [ProcessingStepStatus.Pending]: 'Pending',
  };
  return labels[normalizedStatus];
}

export function getProcessingStepStatusColor(status: ProcessingStepStatus | number | string): string {
  const normalizedStatus = normalizeProcessingStepStatus(status);
  if (!normalizedStatus) {
    return 'bg-gray-100 text-gray-800';
  }

  const colors: Record<ProcessingStepStatus, string> = {
    [ProcessingStepStatus.InProgress]: 'bg-blue-100 text-blue-800',
    [ProcessingStepStatus.Completed]: 'bg-green-100 text-green-800',
    [ProcessingStepStatus.Failed]: 'bg-red-100 text-red-800',
    [ProcessingStepStatus.Skipped]: 'bg-gray-100 text-gray-800',
    [ProcessingStepStatus.Pending]: 'bg-gray-100 text-gray-700 border border-dashed border-gray-300',
  };
  return colors[normalizedStatus];
}

export function getResolveReasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    [ResolveReason.SolutionImplemented]: 'Solution Implemented',
    [ResolveReason.SolutionProvided]: 'Solution Provided',
    [ResolveReason.KnownError]: 'Known Error',
    [ResolveReason.Duplicate]: 'Duplicate',
    [ResolveReason.NoCallerResponse]: 'No Caller Response',
  };
  return labels[reason] ?? reason;
}

export function getOnHoldReasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    [OnHoldReason.AwaitingCaller]: 'Awaiting Caller',
    [OnHoldReason.AwaitingChange]: 'Awaiting Change',
    [OnHoldReason.AwaitingVendor]: 'Awaiting Vendor',
    [OnHoldReason.AwaitingApproval]: 'Awaiting Approval',
  };
  return labels[reason] ?? reason;
}

export function getCancelReasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    [CancelReason.Duplicate]: 'Duplicate',
    [CancelReason.FalseAlarm]: 'False Alarm',
    [CancelReason.CreatedInError]: 'Created in Error',
    [CancelReason.UserCancelled]: 'User Cancelled',
    [CancelReason.NoLongerRequired]: 'No Longer Required',
    [CancelReason.TestIncident]: 'Test Incident',
    [CancelReason.Other]: 'Other',
  };
  return labels[reason] ?? reason;
}
